<template>
  <div class="loan-options-header-container" :class="{ dark, mobile: isMobile }">
    <div
      class="loan-options-header-section"
      v-if="rateTypeSections && showToggle && rateTypeSections?.length > 0"
    >
      <div class="toggle-sections">
        <div
          v-for="rateType in rateTypeSections"
          :key="rateType.rateTypeName"
          class="section-toggle"
          :class="{ active: rateTypeActive === rateType.rateTypeId }"
          @click="$emit('setCurrentRateType', rateType.rateTypeId)"
        >
          <p>{{ rateType.rateTypeName }} Rates</p>
        </div>
      </div>
    </div>
    <div class="loan-options-header-section">
      <div class="title">
        <h3>
          {{ headerText }}
          <span v-if="showToggle" class="tooltip-wrapper"
            ><tooltip :screen-padding="0" class="right-auto">
              <template #toggle>
                <sup><icon icon="info" class="toggle-icon" /></sup>
              </template>
              <div class="repayment-type-tooltip-text">
                <p>{{ currentRateType?.rateTypeName }} Rates</p>
                <p>
                  {{ currentRateType?.rateTypeDescription }}
                </p>
              </div>
            </tooltip></span
          >
        </h3>
      </div>
      <p class="description" v-if="showToggle">
        {{ descriptionText }}
      </p>
      <p class="description" v-else>{{ currentRateType?.rateTypeDescription }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, computed } from "vue";
import { useStore } from "vuex";
import Icon from "@/components/shared/Icon.vue";
import Tooltip from "@/components/pel-loan-configuration/Tooltip.vue";
import useHtmlUtilities from "@/use/htmlUtilities";
import { RateTypeSection } from "@/models/loanConfiguration";

const { isColorDark } = useHtmlUtilities();

const props = defineProps({
  rateTypeActive: {
    type: Number,
    required: true,
    default: 1,
  },
  ratePlanCount: {
    type: Number,
    required: true,
    default: 0,
  },
  isMobile: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits<{
  (e: "setCurrentRateType", rateTypeId: number): void;
}>();

const store = useStore();

const dark = computed<boolean>(() => isColorDark(store.state.lenderData.primaryColor));

const rateTypeSections = computed<Array<RateTypeSection>>(
  () => store.state.loanConfigurationModule.loanOptionsConfiguration.rateTypeSections,
);

const mobile = computed<boolean>(() => props.isMobile);

const showToggle = computed<boolean>(() => rateTypeSections.value?.length > 1);

const headerText = computed<string>(() =>
  mobile.value
    ? `${currentRateType.value?.rateTypeName} Rates`
    : `${currentRateType.value?.rateTypeName} Interest Rate`,
);

const currentRateType = computed<RateTypeSection | undefined>(() =>
  rateTypeSections.value?.find((r: RateTypeSection) => props.rateTypeActive === r.rateTypeId),
);

const otherRateType = computed<string>(
  () =>
    rateTypeSections.value
      ?.find((r: RateTypeSection) => props.rateTypeActive !== r.rateTypeId)
      ?.rateTypeName?.toLowerCase() || "variable",
);

const descriptionText = computed<string>(() =>
  props.ratePlanCount <= 1
    ? `Select your repayment type below or view your ${otherRateType.value} rate options on the other tab.`
    : `Select a term from your options below or view your ${otherRateType.value} rate options on the other tab.`,
);
</script>

<style scoped>
.loan-options-header-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  flex-wrap: wrap;
}

.loan-options-header-section {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: center;
  margin: 0 1rem;
  position: relative;
}

.toggle-sections {
  padding: 0.44px;
  min-height: 39.341px;
  border-radius: 5.246px;
  border: 0.437px solid #ced1d9;
  background: #f2f3f5;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  justify-content: center;
  margin-bottom: 1.125rem;
}

.section-toggle {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-semibold);
  border-radius: 5.25px;
  margin: 6.12px 4.37px;
  min-height: 30.599px;
}

.section-toggle p {
  color: rgba(0, 0, 0, 0.25);
  font-family: var(--header-font);
  font-size: 13.988px;
  font-style: normal;
  font-weight: var(--font-weight-semibold);
  line-height: 17.485px;
  margin-bottom: 0;
}

.section-toggle.active {
  background-color: var(--theme-color, #000000);
  font-weight: var(--font-weight-semibold);
  box-shadow: 0px 0px 5.246px 0px rgba(0, 0, 0, 0.1);
}

.loan-options-header-container:not(.dark) .section-toggle.active p {
  color: #000;
}

.loan-options-header-container:is(.dark) .section-toggle.active p {
  color: #ffffff;
}

.loan-options-header-section .title h3 {
  color: #000;
  text-align: center;
  font-family: var(--header-font);
  font-size: 1.5625rem;
  font-style: normal;
  font-weight: var(--font-weight-bold);
}

.loan-options-header-section .title h3 > span.tooltip-wrapper {
  font-size: 1rem;
}

.tooltip-wrapper .icon {
  font-size: 1rem;
  height: 16px;
  width: 16px;
  color: #000;
}

.tooltip-wrapper .repayment-type-tooltip-text p {
  color: #000;
  font-family: var(--header-font);
  font-size: 0.75rem;
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 15px; /* 125% */
}

.tooltip-wrapper .repayment-type-tooltip-text p:first-of-type {
  font-weight: var(--font-weight-bold);
  margin-bottom: 0px;
  line-height: 21px; /* 175% */
}

.loan-options-header-section .description {
  color: #000;
  text-align: center;
  font-family: var(--header-font);
  font-size: 0.875rem;
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 16px; /* 114.286% */
}

.rate-type-section .tooltip-component {
  z-index: 3;
}

@media (min-width: 292px) {
  .toggle-sections,
  .loan-option-section {
    min-width: 292px;
  }
  .section-toggle {
    width: 100%;
  }
}

@media (max-width: 576.98px) {
  .mobile .title h3 {
    font-size: 1.375rem;
    position: relative;
    margin: 0;
  }

  .mobile .description {
    font-size: 0.75rem;
  }

  .loan-options-header-section .description {
    margin-top: 10px;
  }
}

@media (max-width: 319px) {
  .loan-options-header-container {
    flex-direction: row;
  }
  .loan-options-header-section {
    margin: 0;
  }

  .toggle-sections {
    flex-wrap: wrap;
    width: 100%;
  }

  .section-toggle.active {
    width: calc(100% - 12px);
  }
}
</style>
